import { useState, useEffect, useRef } from 'react';
import { Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

export const CHECKBOX_STYLE = 'border-gray-300 data-[state=checked]:bg-[#290BD9] data-[state=checked]:border-[#290BD9]';

export interface FilterOption {
  value: string;
  label: string;
  color?: string;
  count?: number;
  /** Optional leading visual rendered before the label — used for platform logos in the
   * campaign sidebar filter so Google / Meta options stay visually scannable. */
  icon?: React.ReactNode;
  children?: FilterOption[];
}

export interface FilterSection {
  id: string;
  title: string;
  options: FilterOption[];
}

export interface DatePreset {
  value: string;
  label: string;
  days?: number;
}

const DEFAULT_DATE_PRESETS: DatePreset[] = [
  { value: 'all', label: 'All' },
  { value: 'today', label: 'Today', days: 0 },
  { value: '7days', label: '7 Days', days: 7 },
  { value: '30days', label: '30 Days', days: 30 },
];

export interface FilterDropdownState {
  selections: Record<string, string[]>;
  startDate?: string;
  endDate?: string;
}

interface FilterDropdownProps {
  sections: FilterSection[];
  selectedValues: Record<string, string[]>;
  startDate?: string;
  endDate?: string;
  showDateRange?: boolean;
  datePresets?: DatePreset[];
  activeFilterCount?: number;
  onApply: (filters: FilterDropdownState) => void;
  popoverWidth?: string;
  /** Default selections to restore on Clear (instead of clearing all) */
  defaultSelections?: Record<string, string[]>;
  /** Values that stay selected but aren't shown in the UI */
  hiddenValues?: Record<string, string[]>;
  /** Optional content rendered at the top of the popover before filter sections (e.g. a view-mode toggle) */
  topSlot?: React.ReactNode;
}

/** Collect all leaf values from an option tree (skips parent-only values). */
function getAllLeafValues(options: FilterOption[]): string[] {
  const values: string[] = [];
  for (const opt of options) {
    if (opt.children?.length) {
      values.push(...getAllLeafValues(opt.children));
    } else {
      values.push(opt.value);
    }
  }
  return values;
}

export function FilterDropdown({
  sections,
  selectedValues,
  startDate,
  endDate,
  showDateRange = true,
  datePresets = DEFAULT_DATE_PRESETS,
  activeFilterCount,
  onApply,
  popoverWidth = 'w-[400px]',
  defaultSelections: _defaultSelections,
  hiddenValues,
  topSlot,
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCustomDate, setIsCustomDate] = useState(false);

  // Temporary filter state (not applied until "Apply Filters" is clicked)
  const [tempSelections, setTempSelections] = useState<Record<string, string[]>>(selectedValues);
  const [tempDateRange, setTempDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: startDate ? new Date(startDate + 'T00:00:00') : undefined,
    to: endDate ? new Date(endDate + 'T00:00:00') : undefined,
  });

  // Sync temp state with props when they change externally
  const prevSelectedValuesRef = useRef<string>('');
  useEffect(() => {
    const serialized = JSON.stringify(selectedValues);
    if (serialized !== prevSelectedValuesRef.current) {
      prevSelectedValuesRef.current = serialized;
      setTempSelections(selectedValues);
    }
  }, [selectedValues]);

  useEffect(() => {
    setTempDateRange({
      from: startDate ? new Date(startDate + 'T00:00:00') : undefined,
      to: endDate ? new Date(endDate + 'T00:00:00') : undefined,
    });
  }, [startDate, endDate]);

  const applySelections = (next: Record<string, string[]>) => {
    // Merge hidden values back before applying
    const merged = { ...next };
    if (hiddenValues) {
      for (const [sectionId, values] of Object.entries(hiddenValues)) {
        const visible = merged[sectionId] || [];
        const hidden = values.filter(v => !visible.includes(v));
        merged[sectionId] = [...visible, ...hidden];
      }
    }
    onApply({
      selections: merged,
      startDate: tempDateRange.from ? format(tempDateRange.from, 'yyyy-MM-dd') : undefined,
      endDate: tempDateRange.to ? format(tempDateRange.to, 'yyyy-MM-dd') : undefined,
    });
  };

  const toggleValue = (sectionId: string, value: string) => {
    setTempSelections((prev) => {
      const current = prev[sectionId] || [];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      const next = { ...prev, [sectionId]: updated };
      applySelections(next);
      return next;
    });
  };

  const toggleAll = (section: FilterSection) => {
    setTempSelections((prev) => {
      const allValues = getAllLeafValues(section.options);
      const current = prev[section.id] || [];
      const allSelected = allValues.every((v) => current.includes(v));
      const next = { ...prev, [section.id]: allSelected ? [] : allValues };
      applySelections(next);
      return next;
    });
  };

  const toggleParent = (sectionId: string, option: FilterOption) => {
    if (!option.children?.length) return;
    setTempSelections((prev) => {
      const current = prev[sectionId] || [];
      const childValues = option.children!.map(c => c.value);
      const allChildSelected = childValues.every(v => current.includes(v));
      const updated = allChildSelected
        ? current.filter(v => !childValues.includes(v))
        : [...current.filter(v => !childValues.includes(v)), ...childValues];
      const next = { ...prev, [sectionId]: updated };
      applySelections(next);
      return next;
    });
  };

  const handleDatePresetChange = (value: string) => {
    if (value === 'custom') {
      setIsCustomDate(true);
      return;
    }
    setIsCustomDate(false);
    let newFrom: Date | undefined;
    let newTo: Date | undefined;
    const preset = datePresets.find((p) => p.value === value);
    if (preset && preset.days !== undefined) {
      newTo = new Date();
      newFrom = new Date();
      if (preset.days > 0) {
        newFrom.setDate(newFrom.getDate() - preset.days);
      }
    }
    setTempDateRange({ from: newFrom, to: newTo });
    onApply({
      selections: tempSelections,
      startDate: newFrom ? format(newFrom, 'yyyy-MM-dd') : undefined,
      endDate: newTo ? format(newTo, 'yyyy-MM-dd') : undefined,
    });
  };

  const getSelectedDatePreset = (): string => {
    if (isCustomDate) return 'custom';
    if (!tempDateRange.from || !tempDateRange.to) return 'all';
    const fromStr = format(tempDateRange.from, 'yyyy-MM-dd');
    const toStr = format(tempDateRange.to, 'yyyy-MM-dd');
    const expectedTo = format(new Date(), 'yyyy-MM-dd');
    for (const preset of datePresets) {
      if (preset.days === undefined) continue;
      const expectedFromDate = new Date();
      if (preset.days > 0) {
        expectedFromDate.setDate(expectedFromDate.getDate() - preset.days);
      }
      if (fromStr === format(expectedFromDate, 'yyyy-MM-dd') && toStr === expectedTo) {
        return preset.value;
      }
    }
    return 'custom';
  };

  const isDateRangeInvalid = tempDateRange.from && tempDateRange.to && tempDateRange.from > tempDateRange.to;

  const handleClear = () => {
    const clearTo: Record<string, string[]> = {};
    for (const section of sections) {
      clearTo[section.id] = [];
    }
    applySelections(clearTo);
    setTempSelections(clearTo);
  };

  // Count total active visible filters
  const tempFilterCount = activeFilterCount ??
    Object.entries(tempSelections).reduce((sum, [sectionId, arr]) => {
      // Only count values that are visible options (not hidden)
      const section = sections.find(s => s.id === sectionId);
      if (!section) return sum;
      const visibleValues = getAllLeafValues(section.options);
      return sum + arr.filter(v => visibleValues.includes(v)).length;
    }, 0);

  return (
    // #4624: wrap the trigger + badge together so the badge is a SIBLING of
    // the Button (not a child). The button stays as Radix's asChild trigger
    // (preserves aria + keyboard behavior), and the badge — positioned
    // absolutely against this wrapper — can't influence the button's box
    // model under any parent layout context.
    <div className="relative inline-flex">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-9 w-9 p-0 text-gray-900">
            <Filter className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
      <PopoverContent className={cn(popoverWidth, 'p-0')} align="end">
        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {topSlot && (
            <div>
              {topSlot}
              {(showDateRange || sections.length > 0) && <Separator className="mt-4" />}
            </div>
          )}
          {showDateRange && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Date Range</h3>
              <Tabs value={getSelectedDatePreset()} onValueChange={handleDatePresetChange}>
                <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${datePresets.length + 1}, minmax(0, 1fr))` }}>
                  {datePresets.map((preset) => (
                    <TabsTrigger key={preset.value} value={preset.value}>
                      {preset.label}
                    </TabsTrigger>
                  ))}
                  <TabsTrigger value="custom">Custom</TabsTrigger>
                </TabsList>
              </Tabs>
              {getSelectedDatePreset() === 'custom' && (
                <div className="mt-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={tempDateRange.from ? format(tempDateRange.from, 'yyyy-MM-dd') : ''}
                      max={tempDateRange.to ? format(tempDateRange.to, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd')}
                      onChange={(e) => {
                        const newFrom = e.target.value ? new Date(e.target.value + 'T00:00:00') : undefined;
                        setTempDateRange((prev) => ({ ...prev, from: newFrom }));
                        onApply({
                          selections: tempSelections,
                          startDate: newFrom ? format(newFrom, 'yyyy-MM-dd') : undefined,
                          endDate: tempDateRange.to ? format(tempDateRange.to, 'yyyy-MM-dd') : undefined,
                        });
                      }}
                      className="flex-1 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                    />
                    <span className="text-sm text-gray-500">to</span>
                    <input
                      type="date"
                      value={tempDateRange.to ? format(tempDateRange.to, 'yyyy-MM-dd') : ''}
                      min={tempDateRange.from ? format(tempDateRange.from, 'yyyy-MM-dd') : undefined}
                      max={format(new Date(), 'yyyy-MM-dd')}
                      onChange={(e) => {
                        const newTo = e.target.value ? new Date(e.target.value + 'T00:00:00') : undefined;
                        setTempDateRange((prev) => ({ ...prev, to: newTo }));
                        onApply({
                          selections: tempSelections,
                          startDate: tempDateRange.from ? format(tempDateRange.from, 'yyyy-MM-dd') : undefined,
                          endDate: newTo ? format(newTo, 'yyyy-MM-dd') : undefined,
                        });
                      }}
                      className="flex-1 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                    />
                  </div>
                  {isDateRangeInvalid && (
                    <p className="text-xs text-red-600">Start date must be before end date</p>
                  )}
                </div>
              )}
            </div>
          )}
          {sections.map((section, index) => (
            <div key={section.id}>
              {(index > 0 || showDateRange) && <Separator className="mb-4" />}
              <div className="flex items-center gap-2 mb-3">
                {section.options.length > 1 && (
                  <Checkbox
                    id={`${section.id}-select-all`}
                    className={CHECKBOX_STYLE}
                    checked={getAllLeafValues(section.options).every((v) => (tempSelections[section.id] || []).includes(v))}
                    onCheckedChange={() => toggleAll(section)}
                  />
                )}
                <Label
                  htmlFor={section.options.length > 1 ? `${section.id}-select-all` : undefined}
                  className={cn('text-xs font-semibold uppercase tracking-wide text-gray-500', section.options.length > 1 && 'cursor-pointer')}
                >
                  {section.title}
                </Label>
              </div>
              <div className="space-y-3 ml-6">
                {section.options.length === 0 && (
                  <p className="text-sm text-gray-400">No options available</p>
                )}
                {section.options.map((option) => {
                  if (option.children?.length) {
                    const childValues = option.children.map(c => c.value);
                    const allChildSelected = childValues.every(v => (tempSelections[section.id] || []).includes(v));
                    return (
                      <div key={option.value}>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`${section.id}-${option.value}`}
                            className={CHECKBOX_STYLE}
                            checked={allChildSelected}
                            onCheckedChange={() => toggleParent(section.id, option)}
                          />
                          <Label
                            htmlFor={`${section.id}-${option.value}`}
                            className="flex-1 cursor-pointer text-sm font-medium text-gray-900"
                          >
                            {option.label}
                          </Label>
                        </div>
                        <div className="space-y-2 ml-6 mt-2">
                        {option.children.map((child) => {
                          const isChecked = (tempSelections[section.id] || []).includes(child.value);
                          return (
                            <div key={child.value} className="flex items-center space-x-2">
                              <Checkbox
                                id={`${section.id}-${child.value}`}
                                className={CHECKBOX_STYLE}
                                checked={isChecked}
                                onCheckedChange={() => toggleValue(section.id, child.value)}
                              />
                              <Label
                                htmlFor={`${section.id}-${child.value}`}
                                className="flex-1 cursor-pointer text-sm text-gray-700"
                              >
                                {child.label}
                              </Label>
                            </div>
                          );
                        })}
                        </div>
                      </div>
                    );
                  }

                  const isChecked = (tempSelections[section.id] || []).includes(option.value);
                  return (
                    <div key={option.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`${section.id}-${option.value}`}
                        className={CHECKBOX_STYLE}
                        checked={isChecked}
                        onCheckedChange={() => toggleValue(section.id, option.value)}
                      />
                      <Label
                        htmlFor={`${section.id}-${option.value}`}
                        className={cn(
                          'flex-1 cursor-pointer text-sm font-medium flex items-center gap-2',
                          isChecked && option.color ? option.color : 'text-gray-900'
                        )}
                      >
                        {option.icon}
                        <span>
                          {option.label}
                          {option.count !== undefined && option.count > 0 && (
                            <span className="ml-1 text-gray-500">({option.count})</span>
                          )}
                        </span>
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

        </div>

        {/* Actions */}
        <div className="border-t p-2 bg-gray-50">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            disabled={tempFilterCount === 0}
            className="w-full text-gray-700"
          >
            Clear
          </Button>
        </div>
      </PopoverContent>
      </Popover>
      {tempFilterCount > 0 && (
        <span className="pointer-events-none absolute -top-1 -right-1 h-4 w-4 rounded-full border border-gray-500 bg-white text-gray-500 text-[10px] flex items-center justify-center">
          {tempFilterCount}
        </span>
      )}
    </div>
  );
}
